
import { sendEmailVerification } from "firebase/auth";
import { toast } from "react-hot-toast";

/**
 * Handles the post-authentication flow including email verification,
 * terms acceptance check, and subscription status check
 * 
 * @param {Object} user - Firebase user object
 * @param {Object} userData - User data from Firestore
 * @param {Function} navigate - React Router navigate function
 * @param {string} email - User email (for remember me functionality)
 * @param {Function} setRememberEmail - Function to save email if remember me is checked
 * @param {boolean} isSignup - Whether this is a signup flow (affects messages)
 * @return {boolean} - Returns true if flow was redirected, false otherwise
 */
export const handleAuthFlow = async (user, userData, navigate, email = null, setRememberEmail = null, isSignup = false, addToCart = null, initialPlan = null) => {
  try {
    // Handle remember me if email and function provided
    if (email && setRememberEmail) {
      setRememberEmail(email);
    }

    // Success message
    if (!isSignup) {
      toast.success("Login successful!");
    }

    // Check subscription status
    if (!userData.subscription_status) {
      console.log("User has not paid");
      
      if (isSignup && addToCart && initialPlan) {
        addToCart(initialPlan);
        // addToCart handles navigation to /checkout
      } else {
        navigate("/our-plans");
      }
      return true;
    }

    // Check email verification
    if (!user.emailVerified) {
      // Only send verification email for login, not signup (usually handled elsewhere in signup)
      if (!isSignup) {
        await sendEmailVerification(user, {
          url: `${window.location.origin}/disclaimer`, // dynamically use current URL origin
        });
      }
      navigate("/verify-email");
      return true;
    }
    
    // Check terms acceptance
    if (!userData.accept_terms) {
      console.log("User has not accepted terms");
      navigate("/disclaimer");
      return true;
    }
    
    // If all checks pass, navigate to dashboard
    navigate("/dashboard");
    return true;
  } catch (err) {
    console.error("Auth flow error:", err.message);
    toast.error(`An error occurred during ${isSignup ? 'signup' : 'login'}. Please try again.`);
    return false;
  }
};

/**
 * Handles authentication errors with appropriate error messages
 * 
 * @param {Error} err - Error object from Firebase
 * @param {boolean} isSignup - Whether this is a signup-related error
 */
export const handleAuthError = (err, isSignup = false) => {
  console.error(err.message);
  let msg = isSignup 
    ? "Signup failed. Please try again." 
    : "Login failed. Please check your credentials.";
  
  // Login specific errors
  if (!isSignup) {
    if (err.code === "auth/too-many-requests") {
      msg = "Too many attempts. Please wait a few minutes before trying again.";
    }
    
    if (err.code === "auth/user-not-found") {
      msg = "No user found with that email.";
    }
    
    if (err.code === "auth/wrong-password") {
      msg = "Incorrect password.";
    }
  }
  
  // Signup specific errors
  if (isSignup) {
    if (err.code === "auth/email-already-in-use") {
      msg = "Email already in use.";
    } 
    
    if (err.code === "auth/invalid-email") {
      msg = "Invalid email format.";
    } 
    
    if (err.code === "auth/weak-password") {
      msg = "Password should be at least 6 characters.";
    }
  }
  
  toast.error(msg);
};